import { ConstLocalStorage } from "src/constants/_localStorage";
import { getLocalStoredData } from "./_localStorage";
import { IBranchPermissions } from "src/types/auth";
import Papa from "papaparse";
import * as XLSX from "xlsx";

export const getApiBaseUrl = (): string =>
  (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

export const convertObjectToFormData = (object: any) => {
  let formData = new FormData();
  for (let key in object) {
    formData.append(key, object[key]);
  }

  return formData;
};

export const formatBytes = (bytes: number) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 Byte";
  const i = parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))));
  return Math.round(bytes / Math.pow(1024, i)) + " " + sizes[i];
};

export const formatLongNumbers = (value: number): string => {
  if (value === undefined) {
    return "";
  }
  if (value >= 1000000) {
    return (value / 1000000).toFixed(0) + "M";
  } else if (value >= 1000) {
    return (value / 1000).toFixed(0) + "k";
  } else {
    return value.toFixed(0);
  }
};

// Maps a reference prefix to the corresponding index page route.
export const getReferenceRoute = (reference: string): string => {
  const ref = (reference ?? "").trim();
  const refParam = encodeURIComponent(ref);

  const prefixRoutes: { prefix: string; path: string }[] = [
    { prefix: "J-PUR-", path: "/purchasing/list" },
    { prefix: "OC-", path: "/operation/cost-list" },
    { prefix: "MJE-", path: "/for-accountant/manual-journals" },
    { prefix: "Eve-", path: "/events/list" },
    { prefix: "S-", path: "/sales/list" },
    { prefix: "JRN-", path: "/for-accountant/ledger" },
    { prefix: "COGS-", path: "/inventory/cogs" },
  ];

  const match = prefixRoutes.find(({ prefix }) => ref.startsWith(prefix));
  const path = match?.path ?? "";

  return `${path}?reference=${refParam}`;
};

export const downloadFileFromUrl = (url: string, filename: string): void => {
  // Create an anchor element to trigger the download
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename); // Specifies the download filename
  link.target = "_blank"; // Opens in a new window or tab if it's an image
  document.body.appendChild(link); // Append the link to the document body

  // Programmatically trigger the download by clicking the link
  link.click();

  // Cleanup: Remove the link from the DOM
  link.parentNode?.removeChild(link);
};

export const formatNumber = (input: number) => {
  // Convert input to a number
  const num = Number(input);

  // If it's not a valid number, return the input as is
  if (isNaN(num)) {
    return input;
  }

  // Otherwise, format the number with commas
  return new Intl.NumberFormat().format(num);
};

export const formatNumberWithSeparators = (
  value: number,
  maxFractionDigits = 2,
): string => {
  if (!Number.isFinite(value)) {
    return "";
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: maxFractionDigits,
  }).format(value);
};

export const downloadFile = (file: any, type: string, name: string) => {
  // Convert file data to Blob
  const blob = new Blob([file], { type: `${type};charset=utf-8;` });
  // Create a link element
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = name;
  // Append the link to the body and trigger the download
  document.body.appendChild(link);
  link.click();
  // Clean up
  document.body.removeChild(link);
};

//Check If At Least One Branch Has Permission
export const oneBranchHasPermission = (permissionKey: string) => {
  let hasPermission = false;

  const permissions: IBranchPermissions[] = getLocalStoredData(
    ConstLocalStorage.permissions,
  );

  console.log("per", { permissions });

  permissions?.forEach((permission) => {
    if (permission.permissions.includes(permissionKey)) {
      hasPermission = true;
    }
  });

  return hasPermission;
};

//Check If Specific Branch Has Permission
export const specificBranchHasPermission = (
  permissionKey: string,
  branchId: number,
) => {
  let hasPermission = false;

  const permissions: IBranchPermissions[] = getLocalStoredData(
    ConstLocalStorage.permissions,
  );

  const branchPermissions = permissions?.find(
    (permission) => permission.branch_id === branchId,
  )?.permissions;

  if (branchPermissions?.includes(permissionKey)) {
    hasPermission = true;
  }

  return hasPermission;
};

export const atLeastOneBranchHasPermission = (
  permissions: IBranchPermissions[],
  permissionKey: string,
) => {
  return permissions?.some((branch) =>
    branch.permissions.includes(permissionKey),
  );
};

export const exactBranchHasPermission = (
  permissions: IBranchPermissions[],
  permissionKey: string,
  branchId: number,
) => {
  return (
    permissions
      ?.find((branch) => branch.branch_id === branchId)
      ?.permissions.includes(permissionKey) ?? false
  );
};

export const downloadCsvTemplate = (
  columns: string[],
  filename = "template.csv",
) => {
  if (!Array.isArray(columns) || columns.length === 0) {
    console.error("Columns must be a non-empty array");
    return;
  }

  const data = [columns]; // First row as column headers
  const csvContent = Papa.unparse(data);
  const utf8CsvContent = `\uFEFF${csvContent}`; // Ensure correct encoding

  const blob = new Blob([utf8CsvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url); // Clean up memory
};

export const downloadAsXLS = (csvData: string, fileName: string): void => {
  const parsed = Papa.parse<string[]>(csvData, {
    delimiter: ",",
    skipEmptyLines: true,
  });

  const parsedData = parsed.data;

  if (!Array.isArray(parsedData) || parsedData.length === 0) {
    throw new Error("Invalid or empty CSV data.");
  }

  // Convert numeric strings to actual numbers
  const normalizedData = parsedData.map((row) =>
    row.map((cell) => {
      const trimmed = cell.trim();
      return /^[+-]?\d+(\.\d+)?$/.test(trimmed) ? parseFloat(trimmed) : cell;
    }),
  );

  const worksheet = XLSX.utils.aoa_to_sheet(normalizedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function detectDirection(text: string) {
  const rtlChars = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/g;
  const rtlMatch = text.match(rtlChars);
  const rtlCount = rtlMatch ? rtlMatch.length : 0;
  const totalCount = text.length;

  return rtlCount > totalCount / 2 ? "rtl" : "ltr";
}

export function detectDirectionWithSegmenter(text: string) {
  if (!("Segmenter" in Intl)) {
    // Fallback to regex
    return detectDirection(text);
  }

  const segmenter = new Intl.Segmenter("en", { granularity: "word" });
  const segments = Array.from(segmenter.segment(text));

  let rtlCount = 0;
  let totalCount = 0;

  for (const { segment } of segments) {
    if (/[א-ת\u0600-\u06FF]/.test(segment)) {
      rtlCount++;
    }
    totalCount++;
  }

  return rtlCount > totalCount / 2 ? "rtl" : "ltr";
}

export const toNum = (v: string | number) => Number(v) || 0;

export const isEqualRoundedDecimals = (
  a: number,
  b: number,
  decimals: number,
) => Number(a.toFixed(decimals)) === Number(b.toFixed(decimals));
